import {
  bufferToHex, PrefixedHexString, setLengthLeft, toBuffer,
} from 'ethereumjs-util';

type AccessListItem = {
  address: PrefixedHexString
  storageKeys: PrefixedHexString[]
};

type AccessListBufferItem = [Buffer, Buffer[]];
export type AccessListBuffer = AccessListBufferItem[];
export type TAccessList = AccessListItem[];

export function isAccessListBuffer(
  input: AccessListBuffer | TAccessList,
): input is AccessListBuffer {
  if (input.length === 0) {
    return true;
  }
  const firstItem = input[0];
  if (Array.isArray(firstItem)) {
    return true;
  }
  return false;
}

export function isAccessList(input: AccessListBuffer | TAccessList): input is TAccessList {
  return !isAccessListBuffer(input); // This is exactly the same method, except the output is negated.
}

export class AccessLists {
  public static getAccessListData(accessList: AccessListBuffer | TAccessList) {
    let AccessListJSON;
    let bufferAccessList;
    if (accessList && isAccessList(accessList)) {
      AccessListJSON = accessList;
      const newAccessList: AccessListBuffer = [];

      for (let i = 0; i < accessList.length; i += 1) {
        const item: AccessListItem = accessList[i];
        const addressBuffer = toBuffer(item.address);
        const storageItems: Buffer[] = [];
        for (let index = 0; index < item.storageKeys.length; index += 1) {
          storageItems.push(toBuffer(item.storageKeys[index]));
        }
        newAccessList.push([addressBuffer, storageItems]);
      }
      bufferAccessList = newAccessList;
    } else {
      bufferAccessList = accessList ?? [];
      // build the JSON
      const json: TAccessList = [];
      for (let i = 0; i < bufferAccessList.length; i += 1) {
        const data: any = bufferAccessList[i];
        const address = bufferToHex(data[0]);
        const storageKeys: string[] = [];
        for (let item = 0; item < data[1].length; item += 1) {
          storageKeys.push(bufferToHex(data[1][item]));
        }
        const jsonItem: AccessListItem = {
          address,
          storageKeys,
        };
        json.push(jsonItem);
      }
      AccessListJSON = json;
    }

    return {
      AccessListJSON,
      accessList: bufferAccessList,
    };
  }

  public static verifyAccessList(accessList: AccessListBuffer) {
    for (let key = 0; key < accessList.length; key += 1) {
      const accessListItem = accessList[key];
      const address = <Buffer>accessListItem[0];
      const storageSlots = <Buffer[]>accessListItem[1];
      if ((<any>accessListItem)[2] !== undefined) {
        throw new Error(
          'Access list item cannot have 3 elements. It can only have an address, and an array of storage slots.',
        );
      }
      if (address.length !== 20) {
        throw new Error('Invalid EIP-2930 transaction: address length should be 20 bytes');
      }
      for (let storageSlot = 0; storageSlot < storageSlots.length; storageSlot += 1) {
        if (storageSlots[storageSlot].length !== 32) {
          throw new Error('Invalid EIP-2930 transaction: storage slot length should be 32 bytes');
        }
      }
    }
  }

  public static getAccessListJSON(accessList: AccessListBuffer) {
    const accessListJSON = [];
    for (let index = 0; index < accessList.length; index += 1) {
      const item: any = accessList[index];
      const JSONItem: any = {
        address: `0x${ setLengthLeft(<Buffer>item[0], 20).toString('hex')}`,
        storageKeys: [],
      };
      const storageSlots: Buffer[] = item[1];
      for (let slot = 0; slot < storageSlots.length; slot += 1) {
        const storageSlot = storageSlots[slot];
        JSONItem.storageKeys.push(`0x${ setLengthLeft(storageSlot, 32).toString('hex')}`);
      }
      accessListJSON.push(JSONItem);
    }
    return accessListJSON;
  }
}
